import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import useData from "../../hooks/useData";

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const toDateInputValue = (value) => {
  if (!value) return "";

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  const text = String(value).trim();
  if (!text) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) return text.slice(0, 10);

  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  return "";
};

const buildFormFromExpense = (expense) => ({
  projectId: String(expense?.projectId || expense?.project?.id || ""),
  expenseAccountId: String(
    expense?.expenseAccountId || expense?.expenseAccount?.id || "",
  ),
  expenseDate: toDateInputValue(expense?.expenseDate),
  description: expense?.description || "",
  notes: expense?.notes || "",
  amount: toNumber(expense?.amount),
  minutes: toNumber(expense?.minutes),
  mileage: toNumber(expense?.mileage),
  pst: toNumber(expense?.pst),
  hst: toNumber(expense?.hst),
  gst: toNumber(expense?.gst),
  receipts: Array.isArray(expense?.receipts) ? expense.receipts : [],
  expenseCategory:
    expense?.expenseCategory || expense?.expenseType || "expense",
});

const ExpenseApprovalModal = ({
  open,
  expense,
  initialAction = "approve",
  submitting = false,
  onClose,
  onApprove,
  onReject,
}) => {
  const { getFromServer } = useData();
  const [action, setAction] = useState(initialAction);
  const [rejectionReason, setRejectionReason] = useState("");
  const [projectOptions, setProjectOptions] = useState([]);
  const [expenseAccountOptions, setExpenseAccountOptions] = useState([]);
  const [form, setForm] = useState({
    projectId: "",
    expenseAccountId: "",
    expenseDate: "",
    description: "",
    notes: "",
    amount: 0,
    minutes: 0,
    mileage: 0,
    pst: 0,
    hst: 0,
    gst: 0,
    receipts: [],
    expenseCategory: "expense",
  });

  useEffect(() => {
    if (!open || !expense) return;

    setAction(initialAction || "approve");
    setRejectionReason("");
    setForm(buildFormFromExpense(expense));
  }, [open, expense, initialAction]);

  useEffect(() => {
    if (!open) return;

    let active = true;

    const loadOptions = async () => {
      const [projects, expenseAccounts] = await Promise.all([
        getFromServer({ entityName: "Project" }),
        getFromServer({ entityName: "ExpenseAccount" }),
      ]);

      if (!active) return;

      setProjectOptions(
        Array.isArray(projects)
          ? projects.map((project) => ({
              ...project,
              id: String(project?.id || ""),
            }))
          : [],
      );
      setExpenseAccountOptions(
        Array.isArray(expenseAccounts)
          ? expenseAccounts.map((account) => ({
              ...account,
              id: String(account?.id || ""),
            }))
          : [],
      );
    };

    loadOptions();

    return () => {
      active = false;
    };
  }, [open]);

  const hasProjectOption = projectOptions.some(
    (project) => project.id === form.projectId,
  );
  const hasExpenseAccountOption = expenseAccountOptions.some(
    (account) => account.id === form.expenseAccountId,
  );

  const projectSelectValue = hasProjectOption ? form.projectId : "";

  const expenseAccountSelectValue = hasExpenseAccountOption
    ? form.expenseAccountId
    : "";

  const selectedAccount =
    expenseAccountOptions.find(
      (account) => account.id === form.expenseAccountId,
    ) || null;

  const selectedType =
    selectedAccount?.type || form.expenseCategory || "expense";

  const payload = {
    projectId: form.projectId || null,
    expenseAccountId: form.expenseAccountId || null,
    expenseDate: toDateInputValue(form.expenseDate) || null,
    description: form.description || "",
    notes: form.notes || "",
    amount: toNumber(form.amount),
    minutes: Math.max(0, Math.round(toNumber(form.minutes))),
    mileage: toNumber(form.mileage),
    pst: toNumber(form.pst),
    hst: toNumber(form.hst),
    gst: toNumber(form.gst),
    receipts: Array.isArray(form.receipts) ? form.receipts : [],
    expenseCategory: selectedType,
    expenseType: selectedType,
  };

  const canSubmit =
    !!payload.projectId &&
    !!payload.expenseAccountId &&
    hasProjectOption &&
    hasExpenseAccountOption &&
    (action === "approve" || rejectionReason.trim().length > 0);

  const onSubmit = async () => {
    if (!expense) return;

    if (action === "approve") {
      await onApprove(payload);
      return;
    }

    await onReject(rejectionReason.trim(), payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {action === "approve" ? "Approve Expense" : "Reject Expense"}
      </DialogTitle>
      <DialogContent>
        <Stack
          spacing={2}
          sx={{ mt: 1 }}
        >
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Rep:{" "}
              {expense?.user?.legalName || expense?.user?.name || "Unknown"}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Receipts:{" "}
              {Array.isArray(form.receipts) ? form.receipts.length : 0}
            </Typography>
          </Box>

          <FormControl fullWidth>
            <InputLabel>Decision</InputLabel>
            <Select
              value={action}
              label="Decision"
              onChange={(event) => setAction(event.target.value)}
            >
              <MenuItem value="approve">Approve</MenuItem>
              <MenuItem value="reject">Reject</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Project</InputLabel>
            <Select
              value={projectSelectValue}
              label="Project"
              onChange={(event) =>
                setForm((prev) => ({ ...prev, projectId: event.target.value }))
              }
            >
              {projectOptions.map((project) => (
                <MenuItem
                  key={project.id}
                  value={project.id}
                >
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Expense Account</InputLabel>
            <Select
              value={expenseAccountSelectValue}
              label="Expense Account"
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  expenseAccountId: event.target.value,
                }))
              }
            >
              {expenseAccountOptions.map((account) => (
                <MenuItem
                  key={account.id}
                  value={account.id}
                >
                  {account.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Expense Date"
            type="date"
            value={form.expenseDate}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, expenseDate: event.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <TextField
            label="Description"
            value={form.description}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, description: event.target.value }))
            }
            fullWidth
          />

          {selectedType === "hours" ? (
            <TextField
              label="Minutes"
              type="number"
              value={form.minutes}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, minutes: event.target.value }))
              }
              fullWidth
            />
          ) : null}

          {selectedType === "mileage" ? (
            <TextField
              label="Mileage (KM)"
              type="number"
              value={form.mileage}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, mileage: event.target.value }))
              }
              fullWidth
            />
          ) : null}

          {selectedType === "expense" || selectedType === "hours" ? (
            <TextField
              label="Amount"
              type="number"
              value={form.amount}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, amount: event.target.value }))
              }
              fullWidth
            />
          ) : null}

          {selectedType === "expense" ? (
            <Stack
              direction="row"
              spacing={1}
            >
              <TextField
                label="PST"
                type="number"
                value={form.pst}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, pst: event.target.value }))
                }
                fullWidth
              />
              <TextField
                label="HST"
                type="number"
                value={form.hst}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, hst: event.target.value }))
                }
                fullWidth
              />
              <TextField
                label="GST"
                type="number"
                value={form.gst}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, gst: event.target.value }))
                }
                fullWidth
              />
            </Stack>
          ) : null}

          <TextField
            label="Notes"
            value={form.notes}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, notes: event.target.value }))
            }
            fullWidth
            multiline
            rows={2}
          />

          {action === "reject" ? (
            <TextField
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
              fullWidth
              multiline
              rows={3}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color={action === "approve" ? "success" : "error"}
          onClick={onSubmit}
          disabled={!canSubmit || submitting}
        >
          {action === "approve" ? "Approve" : "Reject"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseApprovalModal;
